import { Document, Font, Page, PDFDownloadLink, PDFViewer, StyleSheet, Text, View } from '@react-pdf/renderer'
import PropTypes from 'prop-types'
import React from 'react'

import TimesNewRomanBoldItalic from '../../../components/PDFGenerator/fonts/Times New Roman Bold Italic.ttf'
import TimesNewRomanBold from '../../../components/PDFGenerator/fonts/Times New Roman Bold.ttf'
import TimesNewRomanItalic from '../../../components/PDFGenerator/fonts/Times New Roman Italic.ttf'
import TimesNewRoman from '../../../components/PDFGenerator/fonts/Times New Roman.ttf'
import { Cell, Row, Table } from '../../../components/PDFGenerator/Table'
import { safeFormatDate, safeFormatTime } from '../../../functions/safe'
import {
  ACT_ORDER_DATE_KZ,
  ACT_ORDER_DATE_RU,
  ACT_ORDER_NUMBER,
  formatStandardValue,
  getFormNumber,
  getOrganizationLines
} from '../actPdfUtils'
import ActPdfFooter from '../ActPdfFooter'

const formatTime = Intl.DateTimeFormat('ru-RU', {
  year: 'numeric',
  month: 'long',
  day: 'numeric',
  hour: 'numeric',
  minute: 'numeric',
  second: 'numeric',
}).format

const formatDate = Intl.DateTimeFormat('ru-RU', {
  year: 'numeric',
  month: 'long',
  day: 'numeric',
}).format

const styles = StyleSheet.create({
  page: {
    padding: '20 50 10 50'
  },
  titleWorks: {
    fontSize: 12,
    fontWidth: 600,
    fontFamily: 'Times New Roman Bold',
    textAlign: 'center',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    marginVertical: 10
  },
  string: {
    fontSize: 10,
    marginVertical: 5
  },
  simpleString: {
    display: 'inline',
    fontSize: '10px'
  },
  table: {
    fontSize: 10,
    fontFamily: 'Times New Roman',
    marginVertical: 5
  },
  sampleBold: {
    fontFamily: 'Times New Roman Bold',
    fontSize: 8
  },
  sampleRegular: {
    fontFamily: 'Times New Roman',
    fontSize: 8
  }
})

Font.register({
  family: 'Times New Roman',
  src: TimesNewRoman,
})
Font.register({
  family: 'Times New Roman Italic',
  src: TimesNewRomanItalic,
})
Font.register({
  family: 'Times New Roman Bold',
  src: TimesNewRomanBold,
})

ActBioTestPDFGenerator.propTypes = PDFDocument.propTypes = {
  data: PropTypes.object,
  name: PropTypes.string,
  kazakhName: PropTypes.string,
  formNumber: PropTypes.string,
}

function PDFDocument({ data, name, kazakhName, formNumber }) {
  const actData = data.data
  const resolvedFormNumber = getFormNumber(data, formNumber)
  const organizationLines = getOrganizationLines(data)
  const schema = [
    {
      key: '1. Объектінің атауы (Наименование объекта)',
      value: actData?.object?.value?.name
    },
    {
      key: '2. Тексеру белгіленгені туралы актінің нөмірі (Номер акта о назначении проверки)',
      value: actData?.number?.value
    },
    {
      key: '3. Тағайындау актісі күні (Дата акта о назначении проверки)',
      value: actData?.date?.value ? safeFormatDate(actData?.date.value) : ' '
    },
    {
      key: '4. Дезинфекциялық камераның атауы (Наименование дезинфекционной камеры)',
      value: actData?.disinfectionChamber?.value
    },
    {
      key: '5. Сынамалар алу мақсаты (Цель отбора)',
      value: actData?.samplingPurpose?.value?.name
    },
    {
      key: '6. Алынған күні мен уақыты (Дата и время отбора)',
      value: actData?.selectionDateTime?.value ? safeFormatTime(actData?.selectionDateTime?.value) : ' '
    },
    {
      key: '7. Жеткізілген күні мен уақыты (Дата и время доставки)',
      value: actData?.deliveryDateTime?.value ? safeFormatTime(actData?.deliveryDateTime?.value) : ' '
    },
    {
      key: '8. Сынама алу әдiсiне нормативтік құжат (Нормативный документ на метод отбора)',
      value: formatStandardValue(actData?.standard?.value) || ' '
    },
    {
      key: '9. Тасымалдау жағдайы (Условия транспортировки)',
      value: actData?.transportationCondition?.value?.name
    },
    {
      key: '10. Сақтау жағдайы (Условия хранения)',
      value: actData?.storageCondition?.value?.name
    },
    {
      key: '11. Қосымша мәліметтер (Дополнительные сведения)',
      value: actData?.additionalInfo?.value
    },
  ]

  return (
    <Document>
      <Page size='A4' style={styles.page}>
        <Table style={styles.table}>
          <Row>
            <Cell></Cell>
            <Cell>
              <Text>Нысанның БҚСЖ бойынша коды</Text>
              <Text>Код формы по ОКУД ___________________</Text>
              <Text>КҰЖЖ бойынша ұйым коды</Text>
              <Text>Код организации по ОКПО _____________</Text>
            </Cell>
          </Row>
          <Row>
            <Cell>
              <Text>Қазақстан Республикасының Денсаулық сақтау министрлігі Министерство здравоохранения Республики Казахстан</Text>
            </Cell>
            <Cell>
              <Text>Қазақстан Республикасының Денсаулық сақтау министрінің {ACT_ORDER_DATE_KZ} {ACT_ORDER_NUMBER} бұйрығымен бекітілген № {resolvedFormNumber}/е нысанды медициналық құжаттама</Text>
            </Cell>
          </Row>
          <Row>
            <Cell>
              <Text>Санитариялық-эпидемиологиялық қызметтiң мемлекеттiк органының атауы Наименование государственного органа санитарно-эпидемиологической службы</Text>
              {organizationLines.length
                ? organizationLines.map((line, index) => (
                  <Text key={index}>{line}</Text>
                ))
                : <Text> </Text>}
            </Cell>
            <Cell>
              <Text>Медицинская документация Форма № {resolvedFormNumber}/у Утверждена приказом Министра здравоохранения Республики Казахстан от {ACT_ORDER_DATE_RU} {ACT_ORDER_NUMBER}</Text>
            </Cell>
          </Row>
        </Table>
        <View style={styles.titleWorks} wrap>
          <Text>{kazakhName}</Text>
          <Text>{name}</Text>
          <Text>№{data.number} от {actData?.dateOfAct?.value ? safeFormatDate(actData.dateOfAct.value) : ' '}</Text>
        </View>
        <Table style={styles.table}>
          {schema.map(el => {
            return <Row>
              <Cell>
                <Text style={{ fontFamily: 'Times New Roman Bold' }}>
                  {el.key}:
                </Text>
              </Cell>
              <Cell>
                <Text>
                  {el.value || ' '}
                </Text>
              </Cell>
            </Row>
          })}
        </Table>
        <Table style={styles.table}>
          {actData?.samples?.value.map(el => {
            return <Row>
              <Cell>
                <Text style={styles.sampleBold}>
                  Үлгіні тiркеу нөмірi (Регистрационный номер образца):
                </Text>
                <Text style={styles.sampleRegular}>
                  {el.registrationNumber?.value || ' '}
                </Text>
              </Cell>
              <Cell>
                <Text style={styles.sampleBold}>
                  Тесттің номері (Номер теста)
                </Text>
                <Text style={styles.sampleRegular}>
                  {el.sample?.value || ' '}
                </Text>
              </Cell>
              <Cell>
                <Text style={styles.sampleBold}>
                  Алынған үлгілер саны (Количество отобранных образцов):
                </Text>
                <Text style={styles.sampleRegular}>
                  {el.sample?.value || ' '}
                </Text>
              </Cell>
              <Cell>
                <Text style={styles.sampleBold}>
                  Бақылау нуктелерінің орыны (Расположение контрольных точек)
                </Text>
                <Text style={styles.sampleRegular}>
                  {el.location?.value || ' '}
                </Text>
              </Cell>

            </Row>
          })}
        </Table>
        <ActPdfFooter data={data} styles={styles} />
      </Page>
    </Document>
  )
}

export default function ActBioTestPDFGenerator(props) {
  return (
    <>
      <PDFViewer style={{ width: '100%', height: 700 }}>
        <PDFDocument {...props} />
      </PDFViewer>
      <PDFDownloadLink document={<PDFDocument {...props} />} fileName={`${formatTime(new Date())}.pdf`}>
        {({ url }) => url ? 'Скачать PDF' : 'Загрузка...'}
      </PDFDownloadLink>
    </>
  )
}
